'use client';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLocale, useTranslations } from 'next-intl';
import Image from 'next/image';
import Link from 'next/link';
import dayjs from 'dayjs';
import 'dayjs/locale/ar';
import { gifImage } from '@/lib/placeholder';
import { getLocalizedPath } from '@/lib/getLocalizedPath';
import { getBlogDetailPath } from '@/lib/slug';
import { imageProps } from '@/lib/seo/media';
import type { BlogArticle } from '@/lib/blogs';

const BLOG_IMAGE_FALLBACK = '/ui/blogs/b1.jpg';

function getBlogImageSrc(image?: string | null) {
  return image || BLOG_IMAGE_FALLBACK;
}

interface BlogArticleClientProps {
  article: BlogArticle;
  relatedArticles: BlogArticle[];
}

export default function BlogArticleClient({
  article,
  relatedArticles: blogs,
}: BlogArticleClientProps) {
  const t = useTranslations('blogs');
  const locale = useLocale();

  const formatDate = (date: string | Date) =>
    dayjs(date).locale(locale === 'ar' ? 'ar' : 'en').format('DD • MMMM');

  const formatCardDate = (date: string | Date) =>
    dayjs(date).locale(locale === 'ar' ? 'ar' : 'en').format('DD•MM•YYYY');

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="px-4 pb-4 pt-8 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          {/* Back link */}
          <motion.div
            className="group mb-4 flex items-center gap-3 text-primary-600 transition-all duration-300 hover:text-primary-700"
            initial={false}
          >
            <Link
              href={getLocalizedPath(locale, '/blogs')}
              className="flex items-center gap-3"
            >
              <span className="grid h-11 w-11 place-items-center rounded-full bg-white text-plum shadow-[inset_0_0_0_1.5px_rgba(85,39,109,.35)]">
                {locale === 'ar' ? (
                  <ChevronRight className="h-5 w-5" />
                ) : (
                  <ChevronLeft className="h-5 w-5" />
                )}
              </span>
              <span className="text-sm font-semibold text-deep">
                {t('blog_posts')}
              </span>
            </Link>
          </motion.div>

          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="text-left lg:max-w-2xl">
              <h1 className="mt-2 text-balance text-[clamp(30px,4.4vw,52px)] font-extrabold leading-[1.35] text-deep">
                {locale === 'ar' ? article.name_ar : article.name_en}
              </h1>
              <p className="mt-4 text-base text-deep/60 sm:text-lg">
                {locale === 'ar'
                  ? article.short_description_ar
                  : article.short_description_en}
              </p>
            </div>

            <div className="flex flex-col rounded-[22px] bg-white px-5 py-4 text-deep shadow-[inset_0_0_0_1px_rgba(57,22,77,.1)] sm:px-6 lg:min-w-[220px]">
              <p className="text-xl font-semibold text-deep">
                {t('by_mila')}
              </p>
              <p className="text-sm text-deep/60">{t('corporate')}</p>
              <div className="mt-3 text-sm font-semibold text-deep">
                {formatDate(article.created_at)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          background: 'linear-gradient(to top, #f0e3ed, #fff)',
        }}
      >
        <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
          <div className="mb-10 w-full">
            <div className="relative h-[280px] w-full overflow-hidden rounded-[30px] shadow-[0_0_0_1px_rgba(217,180,95,.8),0_40px_70px_-50px_rgba(57,22,77,.6)] sm:h-[360px] lg:h-[480px]">
              <Image
                {...imageProps(getBlogImageSrc(article.primary_image))}
                alt={locale === 'ar' ? article.name_ar : article.name_en}
                placeholder="blur"
                blurDataURL={gifImage}
                fill
                sizes="(max-width: 768px) 100vw, (max-width: 1024px) 80vw, 60vw"
                className="object-cover"
              />
            </div>
          </div>

          <article
            className="prose max-w-none ql-editor font-body text-[16.5px] leading-[2] text-deep/[.86] prose-headings:font-head prose-headings:font-extrabold prose-headings:text-deep prose-a:text-plum"
            dangerouslySetInnerHTML={{
              __html:
                locale === 'ar'
                  ? article.description_ar
                  : article.description_en,
            }}
          />

          {/* Bento Box Style (Featured + Grid) */}
          {(article.images?.length ?? 0) > 0 && (
            <div className="my-8">
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                {(article.images || []).map((image, i) => (
                  <div
                    key={i}
                    className={`
                      group relative overflow-hidden rounded-2xl shadow-lg transition-all duration-300 hover:shadow-2xl
                      ${i === 0 ? 'col-span-2 row-span-2' : 'col-span-1 row-span-1'}
                    `}
                  >
                    <img
                      src={image}
                      alt={`${locale === 'ar' ? article.name_ar : article.name_en} ${i + 1}`}
                      className="h-full w-full transform object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-sm font-bold text-deep shadow-lg backdrop-blur-sm">
                      {i + 1}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-12 flex flex-wrap items-center justify-between gap-[18px] rounded-[28px] p-[clamp(22px,3vw,36px)] text-white [background:radial-gradient(120%_90%_at_100%_0%,#6B2FA0,rgba(107,47,160,0)_60%),linear-gradient(160deg,#55276D,#39164D)]">
            <h2 className="text-[clamp(22px,2.6vw,32px)] font-extrabold leading-[1.5]">{locale === 'ar' ? <>جاهز تحتفل؟ <em className="not-italic text-sun">احجز مكانك</em></> : <>Ready to celebrate? <em className="italic text-sun">Book your place</em></>}</h2>
            <Link href={getLocalizedPath(locale, '/reserve')} className="inline-flex min-h-[46px] items-center rounded-full bg-sun px-[22px] text-[15.5px] font-extrabold text-deep shadow-sun">{locale === 'ar' ? 'تصفح الأماكن' : 'Browse places'}</Link>
          </div>

          <h2 className="mb-5 mt-16 flex items-center text-[clamp(22px,2.6vw,30px)] font-extrabold text-deep">
            {t('related_blog_posts')}
            <span className="ml-2 text-deep">—</span>
          </h2>

          <div className="grid grid-cols-1 gap-6 text-left sm:grid-cols-2 lg:grid-cols-4">
            {Array.isArray(blogs) &&
              blogs.map((blog) => {
                const title = locale === 'ar' ? blog.name_ar : blog.name_en;
                const description =
                  locale === 'ar'
                    ? blog.short_description_ar
                    : blog.short_description_en;
                const imageSrc = getBlogImageSrc(blog.primary_image);

                return (
                  <Link
                    href={getBlogDetailPath(locale, blog)}
                    key={blog.id}
                    className="flex h-full flex-col overflow-hidden rounded-[22px] bg-white shadow-[0_1px_0_rgba(57,22,77,.06),0_22px_40px_-30px_rgba(57,22,77,.4)] transition-[transform,box-shadow] duration-500 hover:-translate-y-1 hover:shadow-[0_0_0_1px_rgba(217,180,95,.85),0_30px_50px_-30px_rgba(57,22,77,.5)]"
                  >
                    <div className="relative h-48 w-full overflow-hidden">
                      <Image
                        {...imageProps(imageSrc)}
                        alt={title}
                        placeholder="blur"
                        blurDataURL={gifImage}
                        fill
                        sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 25vw"
                        className="object-cover"
                      />
                    </div>
                    <div className="flex flex-1 flex-col p-4 sm:p-5">
                      <h3 className="mb-2 line-clamp-2 text-[18px] font-bold text-deep">
                        {title}
                      </h3>
                      <div className="mb-3 text-sm text-deep/70">
                        <p className="line-clamp-2">{description}</p>
                        <span className="mt-2 inline-block cursor-pointer font-semibold text-plum hover:underline">
                          {t('read_more')}
                        </span>
                      </div>
                      <div className="mt-auto text-xs text-deep/70">
                        <span className="text-plum">{t('by_mila')}</span>
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          <span>{formatCardDate(blog.created_at)}</span>
                          <span className="mx-1">•</span>
                          <span>
                            {blog.time_to_read}{' '}
                            {locale == 'ar' ? 'دقائق قراءة' : 'Minutes Reading'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
}
